
import constants from '../constants/generic'
import moment from 'moment';
import paramKeys from '@/open-modules/loan-enquiry/constants/param-keys.js';

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
// otherwise
//
// component: () => generic.lazyLoadView(import('@views/my-view')),

import { defineAsyncComponent, createVNode } from "vue";

const lazyLoadView = function (asyncView) {

  const asynchandler = defineAsyncComponent({
    loader: () => asyncView, 
    loadingComponent: require('@/shared/views/loading.vue').default,
    errorComponent: require('@/shared/views/timeout.vue').default,
    delay: 1,
    timeout: 10000,
  });
  
  return Promise.resolve({
    functional: true,
    render(_, { data, children }) {
      // Transparently pass any props or children to the view component
      return createVNode(asynchandler, data, children);
    },
  });
};

/// Returns today date 
/// *****************************
const todayString = function () {
  return (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substring(0, 10);
};

/// Formats currency value to display in UI
/// options: { local: "en-ie", precision: 2, prefix: "€" }
/// **************************************************
const formatCurrencyToUi = function (value, options) {
  const ret = !value || (typeof value !== 'number' && !value.isNumeric()) ? "" : Number(value).toLocaleString(options.locale, { maximumFractionDigits: options.precision, minimumFractionDigits: options.precision});
  return ret === "" ? ret : (options.prefix ? options.prefix : "") + " " + ret;
};

/// Reads a param from configuration
/// ****************************************
const paramValue = function (loanEnquiryConfig, name) {
  return filterValue(loanEnquiryConfig.Params, name);
};

/// Filters array of type [{Name: '', Value: ''}, ...]
/// Can be used with loanEnquiryConfig.Params and referenceData.SystemSettings
/// ****************************************
const filterValue = function(arr, name) {
  if (!arr) return '';
  const param = arr.filter(el => { return el.Name === name; });
  return param.length > 0 ? param[0].Value : '';
};

/// Opens a new window 
/// ****************************************
const openWindow = function(url, target, options) {
  window.open(url, target ?? "_self", options);
};

/// Returns a string from a potencial null value
/// ****************************************
const getString = function (value) {
  return value === null || value === undefined ? "" : value;
};

const isEmptyString = function (value) {
  return value == null || value == undefined || value == "";
};

/// Converts object to array droping empty columns
/// ****************************************
const toArray = function (obj) {
  return Object.entries(obj).reduce((a, [k, v]) => (v ? (a[k] = v, a) : a), {});
};

/// Formats Date to UI Format - Uses node module "moment()"
/// ****************************************
const formatDateToUI = function (val) {
  if (!val) return "";
  return moment(val).format(constants.displayDateFormat);
};

const formatDateToString = function (val) {
  if (!val) return "";
  return moment(val).format(constants.inputDateFormat);
};

/// ParseBoolValue - Parses boolean value received as string
/// ****************************************
const parseBoolValue = function (val) {
  try {
    return val === null || val == undefined ? false : (typeof (val) == 'string' ? eval(val.toLowerCase()) : val);
  }
  catch (e) { return false; }
};

/// Checks is the "required" validator function is present in the rules collection
/// ****************************************
const isRequired = function (rules) {
  if (rules !== undefined) {
    const req = rules.find(el => typeof (el) === 'function' && el.name == 'required');
    return req && req.length > 0;
  }
  return false;
};

/// Clean number function
/// ****************************************
const cleanNumber = function (value) {
  let result = "";
  if (value) {
    let flag = false;
    const arrayValue = value.toString().split("");
    for (var i = 0; i < arrayValue.length; i++) {
      if (Number.isInteger(parseInt(arrayValue[i])) || arrayValue[i] == "-") { // minus sign
        if (!flag) {
          // Remove leading zeros
          if (arrayValue[i] !== "0") {
            result = result + arrayValue[i];
            flag = true;
          }
        }
        else result = result + arrayValue[i];
      }
    }
  }
  return result;
};


const redirectToStart = function () {
  window.location.href =
    window._inputs.inputGlobal.startUrl ?
      window._inputs.inputGlobal.startUrl :
      paramValue(this.loanEnquiryConfig, paramKeys.ExpiredTokenRedirectUrl);
}

const getQueryParam = function (param) {
  // Determine the query string from either the hash or search part of the URL
  let queryString = '';

  if (window.location.hash.includes('?')) {
    // Extract the hash part of the URL
    const hash = window.location.hash;
    // Split the hash to separate the path and the query string
    queryString = hash.split('?')[1];
  } else if (window.location.search) {
    // Use the search part of the URL if available
    queryString = window.location.search.substring(1);
  }

  // Parse the query string
  const params = new URLSearchParams(queryString);

  // Get the value of the specified parameter
  return params.get(param);
}

export default {
  lazyLoadView,
  todayString, 
  paramValue, 
  filterValue,
  formatCurrencyToUi,
  openWindow, 
  getString,
  isEmptyString,
  toArray,
  parseBoolValue,
  isRequired,
  cleanNumber,
  formatDateToUI,
  formatDateToString,
  redirectToStart,
  getQueryParam
};
